import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "guys_sports_session";
const AuthContext = createContext(null);

// Set up axios interceptor to always send token
function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

function storeToken(token) {
  try { localStorage.setItem(TOKEN_KEY, token); } catch {}
}

function clearToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch {}
}

// Create an axios instance with auth header
function authHeaders() {
  const token = getStoredToken();
  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = getStoredToken();
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const resp = await axios.get(`${API}/auth/me`, { headers: authHeaders(), withCredentials: true });
      setUser(resp.data);
    } catch {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: authHeaders(), withCredentials: true });
    } catch {}
    clearToken();
    setUser(null);
  };

  const handleSessionExchange = useCallback(async (sessionId) => {
    try {
      const resp = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
      const data = resp.data;
      // Store token from response or extract from cookie
      if (data.session_token) {
        storeToken(data.session_token);
      }
      setUser(data);
      return true;
    } catch (err) {
      console.error("Auth exchange failed:", err);
      return false;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/auth/me`, { headers: authHeaders(), withCredentials: true });
      setUser(resp.data);
    } catch {}
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, handleSessionExchange, authHeaders }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
